import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompareArrows, Check, X } from "lucide-react";
import { toast } from "sonner";

const KEY = "fodindin:compare";
const MAX = 3;

export function getCompareList() {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
  catch { return []; }
}

function setCompareList(list) {
  localStorage.setItem(KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("fodindin:compare-changed"));
}

/** Toggles an annonce into the comparison drawer (max 3). */
export default function CompareToggleButton({ annonceId }) {
  const [inList, setInList] = useState(false);

  useEffect(() => {
    const sync = () => setInList(getCompareList().includes(annonceId));
    sync();
    window.addEventListener("fodindin:compare-changed", sync);
    return () => window.removeEventListener("fodindin:compare-changed", sync);
  }, [annonceId]);

  const onClick = () => {
    const list = getCompareList();
    if (list.includes(annonceId)) {
      setCompareList(list.filter((i) => i !== annonceId));
      toast.info("Retire du comparateur.");
      return;
    }
    if (list.length >= MAX) {
      toast.error(`Maximum ${MAX} annonces a comparer. Retirez-en une d'abord.`);
      return;
    }
    setCompareList([...list, annonceId]);
    toast.success("Ajoute au comparateur.");
  };

  return (
    <button
      onClick={onClick}
      className={`mt-3 w-full btn-secondary inline-flex items-center justify-center gap-2 ${inList ? "!text-orange-600 !border-orange-300 !bg-orange-50" : ""}`}
      data-testid="compare-toggle-btn"
      type="button"
    >
      <GitCompareArrows size={16} /> {inList ? "Retirer du comparateur" : "Comparer cette annonce"}
    </button>
  );
}

/** Floating drawer visible across the app once 2+ items are selected. */
export function CompareFloatingDrawer() {
  const [list, setList] = useState(getCompareList());

  useEffect(() => {
    const sync = () => setList(getCompareList());
    window.addEventListener("fodindin:compare-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("fodindin:compare-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (list.length < 1) return null;

  const remove = (id) => setCompareList(list.filter((i) => i !== id));
  const clear = () => setCompareList([]);

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9000] bg-white rounded-full shadow-2xl border border-orange-200 px-4 py-2.5 flex items-center gap-3"
         data-testid="compare-drawer">
      <GitCompareArrows size={16} className="text-orange-600" />
      <span className="text-sm font-bold">{list.length} / {MAX}</span>
      <div className="flex items-center gap-1">
        {list.map((id) => (
          <button key={id} onClick={() => remove(id)}
                  className="text-xs px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 inline-flex items-center gap-1 hover:bg-orange-100"
                  data-testid={`compare-item-${id}`}>
            {id.slice(0, 6)}… <X size={10} />
          </button>
        ))}
      </div>
      <Link to={`/comparer?ids=${list.join(",")}`}
            className={`text-sm font-bold inline-flex items-center gap-1 px-3 py-1.5 rounded-full ${list.length >= 2 ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-500 pointer-events-none"}`}
            data-testid="compare-open-btn">
        <Check size={13} /> Comparer
      </Link>
      <button onClick={clear} className="text-xs text-[var(--ink-soft)] hover:text-red-500"
              data-testid="compare-clear-btn" type="button">
        Vider
      </button>
    </div>
  );
}
